/**
 * Integrytas Group - Common Security Module (CSM)
 * Contextual AI Assistance — Smart Paste / Document Upload for data-entry forms.
 *
 * Drop or paste unstructured text (an email, a blob), or drop a PDF / image.
 * The csm-ai gateway parses it against the form's field schema and returns
 * proposed values. NOTHING is saved automatically — the proposed values are
 * shown in a review panel where the user accepts/rejects each field; only on
 * "Apply" are they handed back to the form via onPrefill(). The real save still
 * funnels through the Common Routine + RLS like any other write.
 *
 * Renders nothing when Smart Paste is not enabled for the screen.
 */

import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, FileText, Loader2, Upload, X } from 'lucide-react';
import { aiClient } from '../../lib/ai/ai_client';
import type { AiDocumentInput, AiFieldSchema } from '../../lib/ai/types';

const SUPPORTED_DOC = new Set([
  'application/pdf', 'image/png', 'image/jpeg', 'image/gif', 'image/webp',
]);
const MAX_DOC_BYTES = 8 * 1024 * 1024; // 8 MB guard before base64 inflation

export interface SmartPasteDropzoneProps {
  pageCode:  string;
  fields:    AiFieldSchema[];
  /** Called with the user-accepted subset of proposed values. */
  onPrefill: (values: Record<string, unknown>) => void;
  enabled?:  boolean;
}

async function fileToBase64(file: File): Promise<string> {
  const buf = await file.arrayBuffer();
  const bytes = new Uint8Array(buf);
  let binary = '';
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

export function SmartPasteDropzone({ pageCode, fields, onPrefill, enabled = true }: SmartPasteDropzoneProps) {
  const { t } = useTranslation();
  const [text, setText]       = useState('');
  const [busy, setBusy]       = useState(false);
  const [error, setError]     = useState<string | null>(null);
  const [notes, setNotes]     = useState<string>('');
  const [proposed, setProposed] = useState<Record<string, unknown> | null>(null);
  const [accepted, setAccepted] = useState<Set<string>>(new Set());
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const fieldLabel = useCallback(
    (key: string) => fields.find((f) => f.key === key)?.label ?? key,
    [fields],
  );

  if (!enabled) return null;

  const runExtract = async (payload: { text?: string; document?: AiDocumentInput }) => {
    setBusy(true); setError(null); setProposed(null); setNotes('');
    try {
      const { data, error: e } = await aiClient.extract({ pageCode, fields, ...payload });
      if (e || !data) { setError(e?.message ?? 'CSM.AI.NO_RESULT'); return; }
      const keys = Object.keys(data.values ?? {});
      if (keys.length === 0) { setError(t('ai.smart_paste.nothing_found')); return; }
      setProposed(data.values);
      setAccepted(new Set(keys));         // default: accept all, user can deselect
      setNotes(data.notes ?? '');
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const handleFile = async (file: File) => {
    if (!SUPPORTED_DOC.has(file.type)) { setError(t('ai.smart_paste.unsupported')); return; }
    if (file.size > MAX_DOC_BYTES) { setError(t('ai.smart_paste.too_large')); return; }
    const data_base64 = await fileToBase64(file);
    await runExtract({ document: { media_type: file.type, data_base64, name: file.name } });
  };

  const onDrop = async (e: React.DragEvent) => {
    e.preventDefault(); setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) { await handleFile(file); return; }
    const dropped = e.dataTransfer.getData('text');
    if (dropped) { setText(dropped); await runExtract({ text: dropped }); }
  };

  const toggle = (key: string) => {
    setAccepted((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const apply = () => {
    if (!proposed) return;
    const out: Record<string, unknown> = {};
    for (const k of accepted) if (k in proposed) out[k] = proposed[k];
    onPrefill(out);
    setProposed(null); setText(''); setNotes('');
  };

  return (
    <div className="rounded border border-primary/30 bg-primary/5 p-2">
      <div className="mb-1 flex items-center gap-1 text-csm-xs font-semibold uppercase tracking-wider text-primary">
        <Upload size={11} /> {t('ai.smart_paste.title')}
      </div>

      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => void onDrop(e)}
        className={
          'rounded border border-dashed px-2 py-2 text-csm-xs ' +
          (dragOver ? 'border-primary bg-primary/10' : 'border-csm-border bg-white')
        }
      >
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={t('ai.smart_paste.placeholder')}
          rows={3}
          className="w-full resize-y rounded border border-csm-border px-2 py-1 text-csm-md focus:border-primary focus:outline-none"
        />
        <div className="mt-1 flex items-center gap-2">
          <button
            type="button"
            onClick={() => void runExtract({ text })}
            disabled={busy || !text.trim()}
            className="flex h-6 items-center gap-1 rounded border border-primary bg-primary px-2 text-csm-xs text-white hover:bg-primary-hover disabled:opacity-60"
          >
            {busy ? <Loader2 size={11} className="animate-spin" /> : <FileText size={11} />}
            {t('ai.smart_paste.parse_text')}
          </button>
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={busy}
            className="flex h-6 items-center gap-1 rounded border border-csm-border bg-white px-2 text-csm-xs text-csm-ink-soft hover:text-primary disabled:opacity-60"
          >
            <Upload size={11} /> {t('ai.smart_paste.upload_doc')}
          </button>
          <input
            ref={fileRef}
            type="file"
            accept=".pdf,.png,.jpg,.jpeg,.gif,.webp"
            className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) void handleFile(f); e.target.value = ''; }}
          />
          <span className="text-csm-2xs text-csm-muted">{t('ai.smart_paste.hint')}</span>
        </div>
      </div>

      {error && <div className="mt-1 text-csm-xs text-csm-danger">{error}</div>}

      {proposed && (
        <div className="mt-2 rounded border border-csm-border bg-white p-2">
          <div className="mb-1 text-csm-xs font-semibold text-csm-ink-soft">
            {t('ai.smart_paste.review_title')}
          </div>
          {notes && <div className="mb-1 text-csm-2xs italic text-csm-muted">{notes}</div>}
          <div className="flex flex-col gap-1">
            {Object.entries(proposed).map(([key, val]) => (
              <label key={key} className="flex items-center gap-2 text-csm-sm">
                <input
                  type="checkbox"
                  checked={accepted.has(key)}
                  onChange={() => toggle(key)}
                  className="h-3.5 w-3.5 accent-gray-700"
                />
                <span className="min-w-[8rem] text-csm-muted">{fieldLabel(key)}</span>
                <span className="font-medium text-csm-ink">{String(val)}</span>
              </label>
            ))}
          </div>
          <div className="mt-2 flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setProposed(null)}
              className="flex h-6 items-center gap-1 rounded border border-csm-border bg-white px-2 text-csm-xs text-csm-muted hover:text-csm-ink"
            >
              <X size={11} /> {t('common.cancel')}
            </button>
            <button
              type="button"
              onClick={apply}
              disabled={accepted.size === 0}
              className="flex h-6 items-center gap-1 rounded border border-primary bg-primary px-2 text-csm-xs text-white hover:bg-primary-hover disabled:opacity-60"
            >
              <Check size={11} /> {t('ai.smart_paste.apply', { count: accepted.size })}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
